import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  Link,
  Backdrop,
  LinearProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { unwrapResult } from "@reduxjs/toolkit";
import {
  getBusinessName,
  loginCarrier,
  getCarrier,
} from "../redux/action/carrier";
import { jwtDecode } from "jwt-decode";
import { setTheme } from "../redux/reducers/themeSlice";
import ForgotPasswordModal from "../modals/ForgotPasswordModal";

const Login: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryParams = new URLSearchParams(location.search);
  const businessName = queryParams.get("businessName") || "";
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleSubmit = async () => {
    if (!email || !password) {
      setError("Email and password are required");
      return;
    }
    setLoadingMessage("Signing In...");
    setIsLoading(true);

    setError("");

    const loginData = {
      email,
      password,
    };

    try {
      const loginResponse = await dispatch<any>(loginCarrier(loginData));
      const jwtToken = unwrapResult(loginResponse);
      if (jwtToken) {
        localStorage.setItem("jwtToken", jwtToken);
        const decoded: any = jwtDecode(jwtToken);
        const role = decoded.role;
        localStorage.setItem("role", role);
      }
      const businessNameResponse = await dispatch<any>(getBusinessName(email));
      const businessName = unwrapResult(businessNameResponse);
      const formattedBusinessName = formatBusinessName(businessName);

      if (formattedBusinessName) {
        localStorage.setItem("businessName", formattedBusinessName);
        localStorage.setItem("email", email);
        dispatch<any>(getCarrier(email))
          .then(unwrapResult)
          .then((res: any) => {
            const colorHex = res.color_hex;
            dispatch(
              setTheme({ primaryColor: colorHex, secondaryColor: "#4A90E2" })
            );
            setIsLoading(false);
            navigate(`/${encodeURIComponent(formattedBusinessName)}/dashboard`);
          })
          .catch((err: any) => {
            setIsLoading(false);
            setError(err?.message || "Registration failed. Please try again.");
          });
      } else {
        setError("Business name not found");
      }
    } catch (err: any) {
      setIsLoading(false);
      setError(err?.message || "Login failed. Please try again.");
    }
  };

  const formatBusinessName = (name: string): string => {
    return name
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^\w\-]/g, "");
  };

  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");

  return (
    <>
      <Box
        sx={{
          width: "100%",
          minHeight: "100vh", 
          height: "100%", 
          background: "linear-gradient(-45deg, #ee7752, #e73c7e, #23d5ab, #23a6d5)",
          backgroundSize: "400% 400%",
          animation: "gradientAnimation 20s ease infinite",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          p: isMobile ? 1 : 3,
          overflow: "hidden", 
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            maxWidth: 500,
            width: "100%",
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 3,
            p: isMobile ? 2 : 3,
            mx: isMobile ? 1 : 3,
            textAlign: "center",
            position: "relative",
            zIndex: 1,
            overflowY: "auto", 
            maxHeight: "90vh",
            boxSizing: "border-box",
          }}
        >
          {isLoading && (
            <Backdrop
              sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
              open={isLoading}
              onClick={() => {
                setIsLoading(true);
              }}
            >
              <Box sx={{ width: isMobile ? "80%" : "50%" }}>
                <LinearProgress />
                <Typography
                  fontSize={isMobile ? 14 : 16}
                  fontWeight={500}
                  textAlign="center"
                  mt={1}
                >
                  {loadingMessage}
                </Typography>
              </Box>
            </Backdrop>
          )}
          <Typography
            variant={isMobile ? "h5" : "h4"}
            gutterBottom
            sx={{
              mb: 3,
              fontWeight: "bold",
              opacity: 0,
              animation: "fadeSlideIn 1s ease forwards",
              "@keyframes fadeSlideIn": {
                "0%": { opacity: 0, transform: "translateY(20px)" },
                "100%": { opacity: 1, transform: "translateY(0)" },
              },
            }}
          >
            Login to{" "}
            <Box
              component="span"
              sx={{
                fontFamily: "'Courier New', Courier, monospace",
                color: "#1976d2",
                fontWeight: "bold",
                fontSize: isMobile ? "inherit" : "inherit",
              }}
            >
              AI of Moving
            </Box>
          </Typography>

          <TextField
            fullWidth
            label="Email"
            variant="outlined"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            sx={{ mb: 2 }}
            size={isMobile ? "small" : "medium"}
          />

          <TextField
            fullWidth
            label="Password"
            variant="outlined"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            sx={{ mb: 2 }}
            size={isMobile ? "small" : "medium"}
            InputProps={{
              endAdornment: (
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                  size={isMobile ? "small" : "medium"}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              ),
            }}
          />

          {error && (
            <Typography color="error" sx={{ mb: 2, fontSize: isMobile ? 14 : 16 }}>
              {error}
            </Typography>
          )}

          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            sx={{ width: "100%", py: isMobile ? 1 : 1.5 }}
            size={isMobile ? "medium" : "large"}
          >
            Login
          </Button>

          <Box sx={{ mt: 2, textAlign: "center" }}>
            <Typography variant="body2" fontSize={isMobile ? 14 : 16}>
              Don't have an account?{" "}
              <Link
                href="/register-carrier"
                variant="body2"
                sx={{ textDecoration: "none", color: "primary.main" }}
              >
                Sign Up
              </Link>
            </Typography>
            <Typography variant="body2" mt={1} fontSize={isMobile ? 14 : 16}>
              <Link
                component="button"
                variant="body2"
                onClick={() => setForgotPasswordOpen(true)}
                sx={{ textDecoration: "none", color: "primary.main" }}
              >
                Forgot Password?
              </Link>
            </Typography>
          </Box>
          <ForgotPasswordModal
            open={forgotPasswordOpen}
            onClose={() => setForgotPasswordOpen(false)}
          />
        </Box>
      </Box>
      <style>
        {`
          @keyframes gradientAnimation {
            0% {
              background-position: 0% 50%;
            }
            50% {
              background-position: 100% 50%;
            }
            100% {
              background-position: 0% 50%;
            }
          }
        `}
      </style>
    </>
  );
};

export default Login;